import React, { Suspense, useEffect, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Panel } from 'react-bootstrap';
import _ from 'lodash';

import withAuthentication from '../../hoc/withAuthentication';
import withLayout from '../../hoc/withLayout';

import { useAlertStore } from '../../store/providers/Alert';
import { useUserManagementStore } from '../../store/providers/UserManagement';
import Permission from '../../components/Permission/Permission';

import { breadcrumbAddRoute, breadcrumbRemoveRoute } from '../../store/actions/breadcrumb';

import Spinner from '../../components/Spinner/Spinner';

import * as constants from '../../constants';
import { columns, sorting } from './UserManagementSchema';

const BaseList = React.lazy(() => import('../../components/Lists/BaseList/BaseList'));
const SideMenu = React.lazy(() => import('../../components/SideMenu/SideMenu'));
const PageHeader = React.lazy(() => import('../../components/PageHeader/PageHeader'));
const UserManagementModal = React.lazy(() => import('./UserManagementModal'));

function UserManagement() {    
    const dispatch = useDispatch();

    const { addAlert } = useAlertStore();
    const { selectRecord, listRecords, newRecord, data, clearData } = useUserManagementStore();

    const [isMounted, setIsMounted] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isBusy, setIsBusy] = useState(false);
    const [listData, setListData] = useState([]);

    const pagePermission = constants.PERMISSION_SETTINGS_USERMANAGEMENT;
    const [hasPermission, setHasPermission] = useState(false);
    const [canEdit, setCanEdit] = useState(false);


    const selectedHandler = (user) => {
        setIsEditing(true);

        selectRecord({ userId: user.Id });
    };

    const addedHandler = () => {
        setIsEditing(true);
        newRecord();
    };

    const refreshedHandler = useCallback(() => {
        listRecords({ role: 'fm', status: 1 });
    }, [listRecords]);

    const cancelledHandler = () => {
        setIsEditing(false);
    };

    const submittedHandler = () => {
        setIsEditing(false);
        refreshedHandler();
    };

    useEffect(() => {
        return () => {
            dispatch(breadcrumbRemoveRoute());
        };
    }, [dispatch]);

    useEffect(() => {
        if (!_.isEmpty(data)) {            
            if (data.status === 'fail' || data.status === 'error') {
                setIsEditing(false);
                addAlert({ key: constants.ALERT_SERVER_ERROR, text: <strong>{data.body}</strong>, type: constants.ALERT_TYPE_DANGER });                
                clearData();
            }

            if (data.scope === 'delete' && data.status === 'success') { refreshedHandler(); }
            if (data.status === 'success' && data.scope === 'list') { setListData(data.body); }
            setIsBusy(data.status === 'processing' && (data.scope === 'delete' || data.scope === 'list'));
        }
    }, [data, addAlert, clearData, refreshedHandler]);

    useEffect(() => {
        if (!hasPermission) { return; }
        if (isMounted) { return; }

        dispatch(breadcrumbAddRoute({ key: 'user_management', text: 'User Management', path: window.location.pathname }));
        refreshedHandler();

        setIsMounted(true);
    }, [isMounted, hasPermission, refreshedHandler, dispatch]);

    return (
        <>
            <SideMenu activeKey="settings" />
            <div className="content-body">
                <Permission showDenied={true} pagePermission={pagePermission} hasPermission={hasPermission} setHasPermission={setHasPermission} setCanEdit={setCanEdit}/>
                { hasPermission && isMounted &&
                    <Suspense fallback={<Spinner text="Loading..." />}>
                        {
                            isEditing && <UserManagementModal
                                canEdit={canEdit}
                                show={isEditing}
                                title={`User Management`}
                                cancelled={() => cancelledHandler()}
                                submitted={() => submittedHandler()} />
                        }
                        <div style={{ paddingRight: '15px' }}>
                            <PageHeader title="User Management" />
                            <Panel>
                                <Panel.Body>
                                    <BaseList
                                        compType="User"
                                        keyField="Id"
                                        listData={listData}
                                        isBusy={isBusy}
                                        sorting={sorting}
                                        columns={columns}
                                        hideCopy={true}
                                        hidePrint={true}
                                        hideFilter={true}
                                        hideDelete={true}
                                        permissionEdit={canEdit}
                                        added={() => addedHandler()}
                                        selected={(user) => selectedHandler(user)}
                                        refreshed={() => refreshedHandler()} />
                                </Panel.Body>
                            </Panel>
                        </div>
                    </Suspense>
                }
            </div>
        </>
    );
}

export default withAuthentication(withLayout(UserManagement));
